<template>
  <div class="drive-active-view h-screen w-screen overflow-hidden bg-gray-50 flex items-center justify-center">
    <!-- Zentriertes pulsierendes Element -->
    <button
      @click="showConfirmationModal = true"
      class="w-full max-w-md mx-4 bg-red-50 rounded-3xl p-8 flex flex-col items-center justify-center transition-all duration-300 hover:bg-red-100 active:bg-red-200"
      :disabled="isEnding"
    >
      <div class="animate-pulse bg-[#E9041D] rounded-full p-6 mb-2">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>
      <span v-if="isEnding" class="text-xl font-medium text-[#E9041D] flex items-center">
        <svg class="animate-spin -ml-1 mr-3 h-6 w-6 text-[#E9041D]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Fahrt wird beendet...
      </span>
      <span v-else class="text-xl font-medium text-[#E9041D] text-center">
        Fahrt läuft<br>
        <span class="text-4xl font-bold mt-2 block">{{ formatDuration }}</span>
        <span class="text-sm font-normal text-gray-500 mt-2 block">Tippen zum Beenden</span>
      </span>
    </button>

    <!-- Bestätigungs-Modal -->
    <!-- DEV-228: der eingegebene Stand widerspricht der Historie des Fahrzeugs -->
    <div v-if="mileageCheck" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Kilometerstand prüfen</h3>

        <div class="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm">
          <div class="flex justify-between py-1">
            <span class="text-gray-600">Letzter bekannter Stand</span>
            <span class="font-medium">{{ formatKm(mileageCheck.reference) }} km</span>
          </div>
          <div class="flex justify-between py-1">
            <span class="text-gray-600">Eingegeben</span>
            <span class="font-medium">{{ formatKm(mileageCheck.entered) }} km</span>
          </div>
          <div class="flex justify-between py-1 border-t border-amber-200 mt-1 pt-2">
            <span class="text-gray-600">Unterschied</span>
            <span class="font-medium text-amber-800">
              {{ mileageCheck.kind === 'lower' ? '-' : '+' }}{{ formatKm(mileageCheck.difference) }} km
            </span>
          </div>
        </div>

        <p class="text-gray-600 mb-4 text-sm">
          Bitte den Tacho noch einmal ablesen. Wenn der Wert stimmt, kannst du ihn so übernehmen.
        </p>

        <div class="flex gap-3">
          <button
            @click="correctMileage"
            class="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
            :disabled="isEnding"
          >
            Korrigieren
          </button>
          <button
            @click="confirmMileage"
            class="flex-1 px-4 py-2 bg-[#E9041D] text-white rounded-lg"
            :disabled="isEnding"
          >
            Ja, so ist es
          </button>
        </div>
      </div>
    </div>

    <div v-if="showConfirmationModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Fahrt beenden</h3>
        <p class="text-gray-600 mb-4">
          Möchten Sie die Fahrt wirklich beenden? Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        
        <!-- Kilometer input - always visible first -->
        <div v-if="!kilometersEntered" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Aktueller Kilometerstand</label>
          <input 
            v-model="endMileage"
            type="number"
            placeholder="z.B. 45000"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E9041D] focus:border-transparent"
            :disabled="isEnding"
            @keyup.enter="confirmKilometers"
          />
          <div v-if="mileageError" class="text-red-600 text-sm mt-2">{{ mileageError }}</div>
        </div>
        
        <!-- QR code scanning - only after kilometers entered -->
        <div v-else class="mb-4">
          <div class="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div class="flex items-center">
              <svg class="h-5 w-5 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span class="text-sm text-green-800">Kilometerstand: {{ endMileage }} km</span>
            </div>
          </div>
          
          <label class="block text-sm font-medium text-gray-700 mb-1">QR-Code des Parkplatzes scannen</label>
          <template v-if="!isOfflineDrive">
            <video ref="videoRef" class="w-full rounded border border-gray-300" style="max-height:200px;" autoplay muted playsinline></video>
            <div v-if="isScanning" class="text-gray-500 text-sm mt-2">Kamera wird geöffnet, bitte QR-Code vor die Kamera halten...</div>
            <div v-if="qrError" class="text-red-600 text-sm mt-2">{{ qrError }}</div>
            <div v-if="qrValid" class="text-green-600 text-sm mt-2">QR-Code gültig. Fahrt wird beendet...</div>
          </template>
          <template v-else>
            <div class="text-gray-500 text-sm mt-2">QR-Code-Scan ist im Offline-Modus deaktiviert. Die Fahrt kann offline beendet werden.</div>
          </template>
        </div>
        
        <div class="flex space-x-4">
          <button 
            @click="cancelEndDrive"
            class="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            :disabled="isEnding || isScanning"
          >
            Abbrechen
          </button>
          
          <!-- Button changes based on state -->
          <button 
            v-if="!kilometersEntered"
            @click="confirmKilometers"
            class="flex-1 py-2 px-4 bg-[#E9041D] text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
            :disabled="isEnding || !endMileage"
          >
            Weiter
          </button>
          
          <template v-else-if="isOfflineDrive">
            <button 
              @click="completeDrive"
              class="flex-1 py-2 px-4 bg-[#E9041D] text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              :disabled="isEnding"
            >
              Fahrt offline beenden
            </button>
          </template>
        </div>
      </div>
    </div>

    <!-- End Drive Button -->
    <div class="fixed bottom-0 left-0 right-0 p-4 bg-white border-t">
      <button 
        @click="showConfirmationModal = true"
        class="w-full py-4 px-4 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors text-base font-medium"
      >
        Fahrt beenden
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import api from '../api/axios'
import { BrowserQRCodeReader } from '@zxing/browser'
import { saveOfflineAction, getAllOfflineActions, saveTrackingPoint } from '../services/offlineSync'
import { v4 as uuidv4 } from 'uuid'

interface Vehicle {
  id: number
  name: string
  license_plate: string
}

// Füge ein lokales Interface für Offline-Location ein:
interface OfflineLocation {
  id: number
  name: string
}
// Passe Transport entsprechend an:
interface Transport {
  id: number | string
  vehicle: Vehicle
  start_location: OfflineLocation
  started_at: string
  status: string
}

const router = useRouter()
const route = useRoute()
const transport = ref<Transport | null>(null)
const isEnding = ref(false)
const startTime = ref<Date | null>(null)
const watchId = ref<number | null>(null)
const currentTime = ref<Date>(new Date())
const showConfirmationModal = ref(false)
// DEV-228: gesetzt, wenn der Server den Stand gegen die Historie beanstandet
const mileageCheck = ref<{
  kind: string
  reference: number
  entered: number
  difference: number
} | null>(null)
const mileageConfirmed = ref(false)

const formatKm = (value: number | null | undefined) => new Intl.NumberFormat('de-DE').format(value ?? 0)
const signature = ref<string | null>(null)
const locationUpdateInterval = ref<number | null>(null)
const qrValid = ref(false)
const qrError = ref('')
const scannedCode = ref('')
const scannedParkslotId = ref<number | null>(null)
const videoRef = ref(null)
let codeReader: BrowserQRCodeReader | null = null
const isScanning = ref(false)

// Kilometer tracking refs
const endMileage = ref<number | null>(null)
const kilometersEntered = ref(false)
const mileageError = ref('')

// const props = defineProps<{
//   id: string
// }>()

interface Position {
  latitude: number
  longitude: number
}

const currentPosition = ref<Position | null>(null)

// Computed property für die verfügbaren Standorte
// Entferne die nicht verwendete Variable availableLocations und alle locations.value-Zugriffe
// Entferne alle codeReader.reset-Aufrufe (Zeilen 376, 381)

// Timer für die Aktualisierung der Anzeige
let timerInterval: number | null = null

// Computed property für die Formatierung der Dauer
const formatDuration = computed(() => {
  if (!startTime.value) return '0 Minuten'
  
  const diff = currentTime.value.getTime() - startTime.value.getTime()
  const minutes = Math.floor(diff / 60000)
  const seconds = Math.floor((diff % 60000) / 1000)
  
  if (minutes === 0) {
    return `${seconds} Sekunden`
  } else if (minutes === 1) {
    return `${minutes} Minute ${seconds} Sekunden`
  } else {
    return `${minutes} Minuten ${seconds} Sekunden`
  }
})

// Computed property für die Offline-Fahrt-Erkennung
const isOfflineDrive = computed(() => typeof transport.value?.id === 'string' && isNaN(Number(transport.value.id)))

// GPS-Tracking starten
const startTracking = () => {
  if ('geolocation' in navigator) {
    const options = {
      enableHighAccuracy: false,
      timeout: 30000,
      maximumAge: 10000
    }

    console.log('Starte GPS-Tracking mit Optionen:', options)
    
    watchId.value = navigator.geolocation.watchPosition(
      (position) => {
        console.log('GPS-Position erhalten:', position)
        currentPosition.value = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }
      },
      (error) => {
        console.error('Fehler beim GPS-Tracking:', error)
        if (error.code === error.TIMEOUT) {
          console.log('GPS Timeout - Versuche erneut...')
        }
      },
      options
    )
  } else {
    console.error('Geolocation wird nicht unterstützt')
  }
}

// GPS-Daten an den Server senden
// const sendLocationUpdate = async (position: GeolocationPosition) => {
//   if (!transport.value) return
  
//   try {
//     const locationData = {
//       latitude: position.coords.latitude,
//       longitude: position.coords.longitude,
//       recorded_at: new Date().toISOString()
//     }
    
//     console.log('Sende GPS-Daten:', locationData)
    
//     await api.post(`/api/driver/transports/${transport.value.id}/track`, locationData)
//     console.log('GPS-Daten erfolgreich gesendet')
//   } catch (error) {
//     console.error('Fehler beim Senden der GPS-Daten:', error)
//   }
// }

// Transport-Daten laden
const loadTransport = async () => {
  const transportId = route.params.id
  console.log('Lade Transport mit ID:', transportId)
  
  if (!transportId) {
    console.error('Keine Transport-ID gefunden')
    router.push('/drives')
    return
  }

  // Prüfe, ob es sich um eine Offline-Fahrt (UUID) handelt
  if (typeof transportId === 'string' && isNaN(Number(transportId))) {
    // Versuche aus dem Offline-Cache zu laden
    const actions = await getAllOfflineActions()
    const offline = actions.find(a => a.type === 'startDrive' && a.id === transportId)
    if (offline) {
      transport.value = {
        id: offline.id,
        vehicle: { id: -1, name: 'Offline', license_plate: offline.payload.license_plate },
        start_location: { id: parseInt(offline.payload.start_location_id), name: 'Startort (offline)' },
        started_at: new Date(offline.createdAt).toISOString(),
        status: 'active'
      }
      startTime.value = new Date(offline.createdAt)
      return
    } else {
      console.error('Offline-Transport nicht gefunden')
      router.push('/drives')
      return
    }
  }
  
  try {
    console.log('Sende API-Anfrage für Transport:', transportId)
    const response = await api.get(`/api/driver/transports/${transportId}`, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    
    console.log('Transport API Response:', response.data)
    
    if (response.data && response.data.data) {
      const transportData = response.data.data
      console.log('Transport-Daten empfangen:', transportData)
      
      transport.value = transportData

      if (transportData.status === 'completed') {
        console.log('Transport ist bereits abgeschlossen, navigiere zurück')
        router.push('/drives')
        return
      }

      if (transportData.started_at) {
        startTime.value = new Date(transportData.started_at)
        console.log('Startzeit gesetzt:', startTime.value)
      }
    } else {
      console.error('Ungültiges API-Response-Format:', response.data)
      router.push('/drives')
    }
  } catch (error: any) {
    console.error('Fehler beim Laden des Transports:', error)
    if (error.response) {
      console.error('API Fehlerdetails:', error.response.data)
      console.error('API Status:', error.response.status)
      console.error('API Headers:', error.response.headers)
    }
    router.push('/drives')
  }
}

// Fahrt beenden
const completeDrive = async () => {
  if (!transport.value) {
    console.error('Kein Transport gefunden')
    return
  }
  
  isEnding.value = true
  
  try {
    if (!navigator.onLine) {
      // Offline: Speichere Aktion lokal
      await saveOfflineAction({
        id: uuidv4(),
        type: 'endDrive',
        payload: {
          id: transport.value.id,
          data: {
            end_mileage: endMileage.value
          }
        },
        createdAt: Date.now()
      })
      alert('Fahrtende wurde offline gespeichert und wird synchronisiert, sobald du wieder online bist.')
      isEnding.value = false
      showConfirmationModal.value = false
      router.push('/drives')
      return
    }
    const formData = new FormData()
    
    // Add end mileage - REQUIRED
    if (endMileage.value) {
      formData.append('end_mileage', endMileage.value.toString())
      console.log('Adding end_mileage to request:', endMileage.value)
    } else {
      console.error('No end mileage provided!')
      alert('Bitte geben Sie den Kilometerstand ein.')
      isEnding.value = false
      return
    }
    
    if (signature.value) {
      // Konvertiere base64 zu Blob
      const base64Data = signature.value.split(',')[1]
      const byteCharacters = atob(base64Data)
      const byteArrays = []
      
      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512)
        const byteNumbers = new Array(slice.length)
        
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i)
        }
        
        const byteArray = new Uint8Array(byteNumbers)
        byteArrays.push(byteArray)
      }
      
      const blob = new Blob(byteArrays, { type: 'image/png' })
      formData.append('signature', blob, 'signature.png')
    }
    
    // Add parkslot_id if available from QR scan
    if (mileageConfirmed.value) {
      formData.append('mileage_confirmed', '1')
    }

    if (scannedParkslotId.value) {
      formData.append('end_parkslot_id', scannedParkslotId.value.toString())
      console.log('Adding end_parkslot_id to request:', scannedParkslotId.value)
    }
    
    // Stoppe Standortaktualisierung vor dem API-Call um 422-Fehler zu vermeiden
    stopLocationUpdates()

    console.log('Sende Fahrt-Beendigung')

    const response = await api.post(`/api/driver/transports/${transport.value.id}/complete`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    
    console.log('Fahrt erfolgreich beendet:', response.data)

    // Navigiere zurück zur Übersicht
    router.push('/drives')
  } catch (error) {
    // Der Server beanstandet den Stand. Ohne diesen Zweig wuerde der Fahrer
    // auf "Fahrt beenden" tippen und es passierte sichtbar nichts.
    const response = (error as any)?.response

    if (response?.status === 409 && response.data?.mileage_check) {
      mileageCheck.value = response.data.mileage_check
      isEnding.value = false
      return
    }

    console.error('Fehler beim Beenden der Fahrt:', error)
  } finally {
    isEnding.value = false
    if (!mileageCheck.value) {
      showConfirmationModal.value = false
    }
  }
}

const confirmMileage = async () => {
  mileageConfirmed.value = true
  mileageCheck.value = null
  await completeDrive()
}

const correctMileage = () => {
  mileageCheck.value = null
  mileageConfirmed.value = false
  kilometersEntered.value = false
  endMileage.value = null
}

// Starte die Standortaktualisierung
const startLocationUpdates = () => {
  // Sofort erste Aktualisierung senden
  if (currentPosition.value) {
    updateLocation()
  }
  
  // Dann alle 10 Sekunden aktualisieren
  locationUpdateInterval.value = window.setInterval(() => {
    if (currentPosition.value) {
      updateLocation()
    }
  }, 10000)
}

// Stoppe die Standortaktualisierung
const stopLocationUpdates = () => {
  if (locationUpdateInterval.value !== null) {
    clearInterval(locationUpdateInterval.value)
    locationUpdateInterval.value = null
  }
}

// Aktualisiere den Standort
const updateLocation = async () => {
  try {
    if (!currentPosition.value || !transport.value) return
    const point = {
      latitude: currentPosition.value.latitude,
      longitude: currentPosition.value.longitude,
      recorded_at: new Date().toISOString()
    }
    if (!navigator.onLine) {
      // Offline: Trackingpunkt speichern
      await saveTrackingPoint(String(transport.value.id), point)
      console.log('Trackingpunkt offline gespeichert:', point)
      return
    }
    console.log('Sende Standortaktualisierung:', currentPosition.value)
    const response = await api.post(`/api/driver/transports/${transport.value.id}/track`, point)
    console.log('Standort erfolgreich aktualisiert:', response.data)
  } catch (error: any) {
    console.error('Fehler beim Aktualisieren des Standorts:', error)
    if (error?.response?.status === 422) {
      console.log('Transport nicht mehr aktiv, stoppe Standortaktualisierung')
      stopLocationUpdates()
    }
  }
}

const startQrScanner = async () => {
  qrError.value = ''
  qrValid.value = false
  scannedCode.value = ''
  scannedParkslotId.value = null
  isScanning.value = true
  await nextTick() // Stelle sicher, dass das Video-Element im DOM ist
  if (!videoRef.value) return
  codeReader = new BrowserQRCodeReader()
  try {
    const result = await codeReader.decodeOnceFromVideoDevice(undefined, videoRef.value)
    scannedCode.value = result.getText()
    await validateQrCode(scannedCode.value)
  } catch (e) {
    qrError.value = 'QR-Code konnte nicht gelesen werden.'
  } finally {
    isScanning.value = false
  }
}

const stopQrScanner = () => {
  // codeReader.reset(); // Entferne diesen Aufruf
}

const validateQrCode = async (code: string) => {
  qrError.value = ''
  qrValid.value = false
  scannedParkslotId.value = null
  if (!code) {
    qrError.value = 'Kein QR-Code erkannt.'
    return
  }
  try {
    const response = await api.post('/api/qr/validate', { code })
    if (response.data && response.data.valid === true) {
      qrValid.value = true
      // Capture parkslot_id if available
      if (response.data.parkslot && response.data.parkslot.id) {
        scannedParkslotId.value = response.data.parkslot.id
        console.log('Parkslot detected:', response.data.parkslot)
      }
      // Fahrt automatisch beenden
      await completeDrive()
    } else {
      qrError.value = 'QR-Code ist ungültig.'
    }
  } catch (e) {
    qrError.value = 'Fehler bei der Validierung.'
  }
}

// Validate and confirm kilometers entry
const confirmKilometers = () => {
  mileageError.value = ''
  
  if (!endMileage.value) {
    mileageError.value = 'Bitte geben Sie den Kilometerstand ein.'
    return
  }
  
  if (endMileage.value < 0) {
    mileageError.value = 'Der Kilometerstand kann nicht negativ sein.'
    return
  }
  
  if (endMileage.value > 9999999) {
    mileageError.value = 'Der Kilometerstand erscheint zu hoch.'
    return
  }
  
  kilometersEntered.value = true
  // Start QR scanner after kilometers are confirmed
  if (!isOfflineDrive.value) {
    startQrScanner()
  }
}

// Cancel end drive and reset state
const cancelEndDrive = () => {
  showConfirmationModal.value = false
  kilometersEntered.value = false
  endMileage.value = null
  mileageError.value = ''
  stopQrScanner()
}

// Starte den QR-Scanner nur wenn Kilometer bereits eingegeben wurden
watch(() => showConfirmationModal.value, (open) => {
  if (!open) {
    // Reset state when modal closes
    kilometersEntered.value = false
    endMileage.value = null
    mileageError.value = ''
    stopQrScanner()
  }
})

onMounted(async () => {
  await loadTransport()
  startTracking()
  startLocationUpdates()
  
  // Timer für die Aktualisierung der Anzeige starten
  timerInterval = window.setInterval(() => {
    currentTime.value = new Date()
  }, 1000)
})

onUnmounted(() => {
  // GPS-Tracking beenden
  if (watchId.value !== null) {
    navigator.geolocation.clearWatch(watchId.value)
  }
  
  // Timer beenden
  if (timerInterval !== null) {
    clearInterval(timerInterval)
  }

  stopLocationUpdates()
  stopQrScanner()
})
</script> 